import { FC } from "react";
import { makeStyles } from "tss-react/mui";
import { MiSkeleton, MiStackedTypography } from "@miplatform/components";
import {
  DATE_TIME_FORMATS,
  ORDER_STATUS,
  ORDER_STATUS_DISPLAY,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { IOrder } from "@miplatform/types";
import { getFormattedDate } from "@miplatform/utils";
import { EntityStatusListItemView } from "@mibuildweb/components";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";

export const OrderSearchOptionPopperContent: FC<
  OrderSearchOptionPopperContentProps
> = ({ order }) => {
  const { classes } = useStyles({ color: order.orderTemplateColor });

  return (
    <Box className={classes.popperContent}>
      <Box className={classes.popperHeader}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.CAPTION}
          className={classes.headerText}
        >
          {order.orderTemplateName}
        </Typography>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.CAPTION}
          className={classes.headerText}
        >
          {ORDER_STATUS_DISPLAY[order.orderStatus]}
        </Typography>
      </Box>
      <Box className={classes.addressSection}>
        <Typography variant={TYPOGRAPHY_VARIANTS.HD2}>
          {order.home?.address?.addressLine1 || " "}
        </Typography>
        <Typography className={classes.addressDetails}>
          {order.home
            ? `${order.home?.address?.city || ""} | ${
                order.home?.communityName || ""
              } | ${order.home?.builderName || ""}`
            : ""}
        </Typography>
      </Box>
      <Box className={classes.dateSection}>
        <Box className={classes.dateContainer}>
          <MiStackedTypography
            title="Sch. Date"
            description={
              order.requestedStartDate
                ? getFormattedDate(
                    DATE_TIME_FORMATS.FORMAT_M_D_YY,
                    order.requestedStartDate
                  )
                : "-"
            }
            descriptionProps={{ fontWeight: "bold" }}
          />
          <MiStackedTypography
            title="Start Date"
            description={
              order.approvedStartDate
                ? getFormattedDate(
                    DATE_TIME_FORMATS.FORMAT_M_D_YY,
                    order.approvedStartDate
                  )
                : "-"
            }
            descriptionProps={{ fontWeight: "bold" }}
          />
          <MiStackedTypography
            title="Complete Date"
            description={
              order.completeDate
                ? getFormattedDate(
                    DATE_TIME_FORMATS.FORMAT_M_D_YY,
                    order.completeDate
                  )
                : "-"
            }
            descriptionProps={{ fontWeight: "bold" }}
          />
        </Box>
      </Box>
      <Box>
        {order.workOrders ? (
          <>
            <Box className={classes.ordersHeader}>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                fontWeight={600}
              >
                Work Orders
              </Typography>
              <Typography
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                fontWeight={600}
              >
                {
                  order.workOrders.filter(
                    (workOrder) => workOrder.status === ORDER_STATUS.COMPLETE
                  ).length
                }
                /{order.workOrders.length}
              </Typography>
            </Box>
            <Box className={classes.ordersWrapper}>
              <Box className={classes.ordersList}>
                {order.workOrders.map((workOrder) => (
                  <EntityStatusListItemView
                    key={workOrder.id}
                    entity={workOrder}
                    getDisplayName={(workOrder) =>
                      workOrder.templateName || "-"
                    }
                    getDetail={(workOrder) => {
                      const completedDate = workOrder.completeDate
                        ? getFormattedDate(
                            DATE_TIME_FORMATS.FORMAT_M_D_YY,
                            new Date(workOrder.completeDate)
                          )
                        : "--/--/--";
                      return `Complete: ${completedDate}`;
                    }}
                    getStatus={(workOrder) => workOrder.status}
                    isWorkOrder
                  />
                ))}
              </Box>
            </Box>
          </>
        ) : (
          <Box>
            <MiSkeleton height={100} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface OrderSearchOptionPopperContentProps {
  order: IOrder;
}

const useStyles = makeStyles<{ color?: string }>()((theme, { color }) => ({
  popperContent: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(2),
    minWidth: 288,
    boxShadow: theme.shadows[4],
  },
  popperSection: {
    marginBottom: theme.spacing(1.5),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  popperHeader: {
    height: 24,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1.5),
    borderTopLeftRadius: theme.spacing(1.5),
    backgroundColor: color || theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    color: theme.palette.common.white,
  },
  addressSection: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  addressDetails: {
    color: theme.palette.grey.medium,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.fontWeight.regular,
  },
  dateSection: {
    display: "flex",
    gap: theme.spacing(4),
    backgroundColor: theme.palette.grey.mediumLight,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  dateContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  ordersHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
  },
  ordersWrapper: {
    backgroundColor: theme.palette.common.white,
    borderBottomLeftRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
  },
  ordersList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2, 2, 2),
    maxHeight: 200,
    overflowY: "auto",
    borderBottomLeftRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
  },
}));
